import { useTranslation } from 'react-i18next';
import { APP_DEFAULT_TZ, formatLongDate } from '@utils/dateUtils';
import type { IOrderRow } from '@interfaces/IFinance';
import { fmtUsd } from '../format';
import { StatusPill } from './StatusPill';
import styles from './OrdersTab.module.css';

interface IOrdersTabProps {
  orders: IOrderRow[];
}

export function OrdersTab({ orders }: IOrdersTabProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';

  return (
    <section className={styles.panel}>
      <header className={styles.panelHead}>
        <div>
          <h2 className={styles.panelTitle}>{t('finance.orders.purchaseOrders')}</h2>
          <p className={styles.panelSubtitle}>{t('finance.orders.purchaseOrdersSubtitle')}</p>
        </div>
        <button type="button" className={styles.newBtn}>
          <span aria-hidden="true">+</span> {t('finance.orders.newOrder')}
        </button>
      </header>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t('finance.orders.colOrderId')}</th>
            <th>{t('finance.orders.colSupplier')}</th>
            <th>{t('finance.orders.colMaterial')}</th>
            <th className={styles.alignRight}>{t('finance.orders.colQuantity')}</th>
            <th className={styles.alignRight}>{t('finance.orders.colAmount')}</th>
            <th>{t('finance.orders.colEta')}</th>
            <th className={styles.alignCenter}>{t('finance.orders.colStatus')}</th>
            <th className={styles.alignCenter}>{t('finance.orders.colLink')}</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((row) => (
            <tr key={row.id}>
              <td className={styles.mono}>{row.id}</td>
              <td>{row.supplier}</td>
              <td className={styles.muted}>{row.material}</td>
              <td className={styles.alignRight}>{row.quantityLabel}</td>
              <td className={`${styles.alignRight} ${styles.bold}`}>{fmtUsd(row.amountUsd)}</td>
              <td className={styles.muted}>{formatLongDate(row.eta, lang, APP_DEFAULT_TZ)}</td>
              <td className={styles.alignCenter}>
                <StatusPill status={row.status} />
              </td>
              <td className={styles.alignCenter}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label={t('finance.orders.openAria', { id: row.id })}
                >
                  ↗
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
